import { DeleteOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import {
  Button,
  Card,
  Col,
  Form,
  FormInstance,
  Input,
  message,
  Row,
  Spin,
  Table,
  Typography,
} from "antd";
import { ColumnType } from "antd/lib/table/interface";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import {
  Company,
  CompanyExclusionList,
  Customer,
  FormFieldConfig,
} from "../helpers/shared-interfaces";
import {
  convertCustomersToSelectOptions,
  formatSnakeCase,
  handleValidationErrors,
  request,
  responseHandler,
} from "../helpers/utilities";
import { exclusionListTypeOptions, statementTypeOptionsForExclusionList } from "./ExclusionLists";
import { ASSIGN_ENTITY_TO_EXCLUSION_LIST, GET_EXCLUSION_LIST, REMOVE_ENTITY_FROM_EXCLUSION_LIST, SEARCH_COMPANY, SEARCH_CUSTOMER, UPDATE_EXCLUSION_LIST } from "../gql/exclusion-list";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";



const { Text } = Typography;

export const ExclusionListUI = (props: {
  type?: string;
  reference: React.RefObject<FormInstance<any>>;
  results: any;
  onSearch: () => void;
  searchTerm: string;
  isSearching?: boolean;
  updateSearchTerm: (searchTerm: string) => void;
}): JSX.Element => {
  const isCustomer = props.type === "customer";

  const fieldId = isCustomer ? "customerId" : "companyId";
  const label = `Select ${isCustomer ? "Customer" : "Client"}`;
  const placeholder = `Search ${isCustomer ? "customer" : "company"} by name`;
  const selectOptions = isCustomer ? convertCustomersToSelectOptions(props.results) : props.results;

  const fields: FormFieldConfig[] = [
    {
      id: fieldId,
      name: fieldId,
      inputType: "search",
      rule: "string",
      label,
      placeholder,
      required: true,
      selectOptions,
      changeHandler: props.updateSearchTerm,
      value: props.searchTerm,
      isSearching: props.isSearching,
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <Spin spinning={props.isSearching}>
      <Row>
        <Col span={24}>
          <BaseForm
            layout="vertical"
            reference={props.reference}
            fieldsToHide={[]}
            fields={fields}
          />
        </Col>
      </Row>
    </Spin>
  );
};

export const UpdateExclusionListUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  initialValue: CompanyExclusionList | null;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "name",
      name: "name",
      label: "Name",
      inputType: "string",
      placeholder: "Enter exclusion name",
      rule: "string",
      required: true,
      elementStyle: {
        width: "100%",
      },
      initialValue: props.initialValue?.exclusionList?.name,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "exclusionListType",
      name: "exclusionListType",
      label: "Exclsuion List Type",
      inputType: "select",
      selectOptions: exclusionListTypeOptions,
      placeholder: "Select Exclusion type",
      rule: "string",
      required: true,
      elementStyle: {
        width: "100%",
      },
      initialValue: props?.initialValue?.exclusionList?.exclusionListType,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "statementType",
      name: "statementType",
      label: "Statement Type",
      inputType: "select",
      selectOptions: statementTypeOptionsForExclusionList,
      placeholder: "Select statement type",
      rule: "string",
      required: true,
      elementStyle: {
        width: "100%",
      },
      initialValue: props?.initialValue?.exclusionList?.statementType,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "description",
      name: "description",
      label: "Description",
      inputType: "textArea",
      placeholder: "Describe Exclusion List",
      rule: "string",
      required: true,
      elementStyle: {
        width: "100%",
      },
      initialValue: props.initialValue?.exclusionList?.description,
      colConfig: {
        span: 24,
      },
    },
  ];
  return (
    <Spin spinning={false}>
      <Row>
        <Col span={24}>
          <BaseForm
            layout="vertical"
            reference={props.reference}
            fieldsToHide={[]}
            fields={fields}
          ></BaseForm>
        </Col>
      </Row>
    </Spin>
  );
};

interface State {
  id: string | number;
  isLoading: boolean;
  companyExclusionList: CompanyExclusionList | null;
  showAddModal: boolean;
  searchTerm: string;
  isSearching: boolean;
  companies: Company[];
  customers: Customer[]
  showUpdateExclusionListModal: boolean;
  modalType?: string
  isFiltering: boolean;
  filteredList?: any
  filterBy: string;
}

interface RouteParams {
  id: string;
}

export function getColumns<T extends Company | Customer>(
  removeExclusionList: (
    entityId: string,
    exclusionListId: string,
    entityType: string
  ) => void,
  exclusionListId: string
): ColumnType<T>[] {
  return [
    {
      title: "Petra Id",
      dataIndex: "petraId",
      key: "petraId",
      width: "50%",
      render(text: string, record: T): JSX.Element {
        return <Text>{record.petraId}</Text>;
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "50%",
      render(text: string, record: T): JSX.Element {
        const displayName = "fullname" in record ? (record as Customer).fullname : (record as Company).name;
        return <Text>{displayName}</Text>;
      },
    },
    {
      title: "Remove",
      dataIndex: "entity",
      key: "entity",
      align: "center" as const,
      render(_, record: T): JSX.Element {
        return (
          <div>
            <NavLink
              id="view-interaction-btn"
              onClick={(
                e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
              ): void => {
                e.preventDefault();
                const entityType: string =
                  "fullname" in record ? "customer" : "client";
                removeExclusionList(record.id, exclusionListId, entityType);
              }}
              to="#"
            >
              <DeleteOutlined />
            </NavLink>
          </div>
        );
      },
    },
  ];
}

export default class ViewExclusionList extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  clientExclusionReference: RefObject<FormInstance>;
  updateExclusionReference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      id: "",
      searchTerm: "",
      isSearching: false,
      isLoading: true,
      showAddModal: false,
      companyExclusionList: null,
      companies: [],
      customers: [],
      showUpdateExclusionListModal: false,
      modalType: "",
      filterBy: "",
      isFiltering: false,
      filteredList: null
    };
    this.clientExclusionReference = React.createRef<FormInstance>();
    this.updateExclusionReference = React.createRef<FormInstance>();
    this.filterList = this.filterList.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.clientExclusionReference = ref;
  };

  getExclusionListData = () => {
    request({
      query: GET_EXCLUSION_LIST,
      variables: { exclusionListId: this.state.id },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        companyExclusionList: data.companyExclusionLists,
        isLoading: false,
      });
    });
  };

  toggleAddModal = (type?: string) => {
    this.setState({ showAddModal: !this.state.showAddModal, modalType: type });
  };

  assignEntityToExclusionList = async () => {
    if (!this.clientExclusionReference.current) return;

    const fieldsToBeValidated: string[] = ["companyId", "customerId"];
    let data;
    try {
      data = await this.clientExclusionReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }
    this.setState({ isLoading: true });
    request({
      query: ASSIGN_ENTITY_TO_EXCLUSION_LIST,
      variables: { ...data, exclusionListId: this.state.id },
    })
      .then((): void => {
        message.success("List updated successfully")
        this.toggleAddModal();
        this.getExclusionListData();
      })
      .catch((error) => {
        message.error("An error occured");
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  removeEntityFromExclusionList = async (
    entityId: string,
    exclusionListId: string,
    entityType: string
  ) => {
    this.setState({ isLoading: true });

    const variables = {
      exclusionListId,
      companyId: entityType === "client" ? entityId : undefined,
      customerId: entityType === "customer" ? entityId : undefined,
    };

    request({
      query: REMOVE_ENTITY_FROM_EXCLUSION_LIST,
      variables
    })
      .then((): void => {
        message.success(`${entityType} removed from list successfully`)
        this.getExclusionListData();
      })
      .catch((error) => {
        message.error("An error occured");
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  updateSearchTerm = (searchTerm: string) => {
    this.setState({
      searchTerm,
    }, () => {
      this.state.modalType === "customer"
        ? this.handleCustomerSearch()
        : this.handleCompanySearch()
    });
  };

  handleCompanySearch = debounce(async (): Promise<void> => {
    this.triggerCompanySearch();
  }, DEBOUNCE_TIME);

  handleCustomerSearch = debounce(async (): Promise<void> => {
    await this.triggerCustomerSearch();
  }, DEBOUNCE_TIME);

  triggerCustomerSearch = async (): Promise<void> => {
    const { searchTerm: q } = this.state;
    interface Response {
      count: number;
      results: Customer[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER,
        variables: { q, page: 1 },
      });

      const { err, mutation } = responseHandler<Response>(r, "customerSearch");
      if (err) {
        message.error(`customer search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          customers: mutation.results,
        });
      }
    } catch (error) {
      message.error(`customer search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSearching: false })
    }
  };

  triggerCompanySearch = async (): Promise<void> => {
    const { searchTerm: q } = this.state;
    interface Response {
      count: number;
      results: Company[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_COMPANY,
        variables: { q, page: 1 },
      });

      const { err, mutation } = responseHandler<Response>(r, "companySearch");

      if (err) {
        message.error(`company search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          companies: mutation.results,
        });
      }
    } catch (error) {
      message.error(`company search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  toggleUpdateExclusionListModal = () => {
    this.setState((prevState) => ({
      showUpdateExclusionListModal: !prevState.showUpdateExclusionListModal,
    }));
  };

  updateExclusionList = async () => {
    if (!this.updateExclusionReference.current) return;

    const fieldsToBeValidated: string[] = ["name", "statementType", "exclusionListType", "description"];
    let data;
    try {
      data = await this.updateExclusionReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }
    this.setState({ isLoading: true });
    request({
      query: UPDATE_EXCLUSION_LIST,
      variables: {
        ...data,
        id: this.state.companyExclusionList?.exclusionList?.id ?? this.state.id,
      },
    })
      .then((): void => {
        message.success("Exclusion list updated successfully");
        this.toggleUpdateExclusionListModal();
        this.getExclusionListData();
      })
      .catch((error) => {
        message.error("An error occured");
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  componentDidMount(): void {
    this.setState(
      { id: this.props.match.params.id, isLoading: true },
      (): void => {
        this.getExclusionListData();
      },
    );
  }

  async filterList(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    e.persist();
    const filterBy: string = e.target.value;
    this.setState({ filterBy, isFiltering: true });

    if (filterBy.length === 0) {
      this.setState({ isFiltering: false, filteredList: [] });
      return;
    }

    const filteredCompanies = this.state.companyExclusionList?.exclusionList.companies?.filter((company) =>
      company.petraId.toLowerCase().includes(filterBy.toLowerCase()) ||
      company.name.toLowerCase().includes(filterBy.toLowerCase())
    );

    const filteredCustomers = this.state.companyExclusionList?.exclusionList?.customers?.filter((customer) =>
      customer.petraId.toLowerCase().includes(filterBy.toLowerCase()) ||
      customer.fullname.toLowerCase().includes(filterBy.toLowerCase())
    );

    const filteredList = [
      ...(filteredCompanies || []),
      ...(filteredCustomers || []),
    ];

    this.setState({ filteredList });
  }



  render(): JSX.Element | null {
    const {
      isLoading,
      companyExclusionList,
      companies,
      customers,
      modalType,
      searchTerm,
      isSearching,
      showAddModal,
      showUpdateExclusionListModal,
      isFiltering,
      filteredList
    } = this.state;
    const tableData = [
      ...(companyExclusionList?.exclusionList.companies?.map((data) => ({
        ...data,
        key: data.id,
        entityType: "client" as const,
      })) || []),
      ...(companyExclusionList?.exclusionList.customers?.map((data) => ({
        ...data,
        key: data.id,
        entityType: "customer" as const,
      })) || []),
    ];

    return (
      <Spin spinning={isLoading}>
        <Row style={{ marginBottom: "20px" }}>
          <Col span={10}> </Col>
          <Col span={14} style={{ textAlign: "right" }}>
            <Button
              className="base-button"
              size="middle"
              data-testid="create-exclusion-list"
              style={{ marginRight: "5px" }}
              onClick={this.toggleUpdateExclusionListModal}
            >
              Edit
            </Button>
            <Button
              className="success-btn"
              size="middle"
              style={{ marginRight: "5px" }}
              data-testid="add-client-to-exclusion-btn"
              onClick={() => this.toggleAddModal("client")}
            >
              Add client
            </Button>
            <Button
              className="success-btn"
              size="middle"
              data-testid="add-client-to-exclusion-btn"
              onClick={() => this.toggleAddModal("customer")}
            >
              Add customer
            </Button>
          </Col>
        </Row>
        <Card>
          <Row style={{ marginBottom: 10 }}>
            <Col span={4}>
              <Text>Name</Text>
            </Col>
            <Col span={20}>
              <Text style={{ textTransform: "capitalize" }}>
                {formatSnakeCase(
                  companyExclusionList?.exclusionList.name || "",
                )}
              </Text>
            </Col>
          </Row>
          <Row style={{ marginBottom: 10 }}>
            <Col span={4}>
              <Text>Exclusion Type</Text>
            </Col>
            <Col span={20}>
              <Text style={{ textTransform: "capitalize" }}>
                {companyExclusionList?.exclusionList.exclusionListType || ""}
              </Text>
            </Col>
          </Row>
          <Row style={{ marginBottom: 10 }}>
            <Col span={4}>
              <Text>Statement Type</Text>
            </Col>
            <Col span={20}>
              <Text style={{ textTransform: "capitalize" }}>
                {formatSnakeCase(
                  companyExclusionList?.exclusionList.statementType || "",
                )}
              </Text>
            </Col>
          </Row>
          <Row style={{ marginBottom: 30 }}>
            <Col span={4}>
              <Text>Description</Text>
            </Col>
            <Col span={20}>
              <Text>{companyExclusionList?.exclusionList.description}</Text>
            </Col>
          </Row>
          <Row>
            <Col span={12}>
              <div className="filter-list-container" style={{}}>
                <Form.Item>
                  <Input.Search
                    size="large"
                    type="text"
                    placeholder="Filter by petra id, name..."
                    onChange={this.filterList}
                  />
                </Form.Item>
              </div>
            </Col>
            <Col span={20}>
              {isLoading ? (
                <></>
              ) : (
                <Table
                  bordered={true}
                  columns={getColumns(this.removeEntityFromExclusionList, this.state.id as string)}
                  dataSource={isFiltering ? filteredList : tableData}
                />
              )}
            </Col>
          </Row>
        </Card>
        {showAddModal &&
          ModalCreator({
            title: `Add to Exclusion List`,
            okType: "primary",
            okText: `Add ${modalType}`,
            showModal: showAddModal,
            isLoading: isLoading,
            onOkay: this.assignEntityToExclusionList,
            toggleModal: this.toggleAddModal,
            inscription: ExclusionListUI({
              type: modalType,
              reference: this.clientExclusionReference,
              onSearch: modalType === "customer" ? this.handleCustomerSearch : this.handleCompanySearch,
              searchTerm,
              isSearching,
              results: modalType === "customer" ? customers : companies,
              updateSearchTerm: this.updateSearchTerm,
            }),
          })}

        {showUpdateExclusionListModal &&
          ModalCreator({
            title: "Update Exclusion List",
            okType: "primary",
            okText: "Update Exclusion",
            showModal: showUpdateExclusionListModal,
            isLoading: isLoading,
            onOkay: this.updateExclusionList,
            toggleModal: this.toggleUpdateExclusionListModal,
            inscription: UpdateExclusionListUI({
              reference: this.updateExclusionReference,
              initialValue: companyExclusionList,
            }),
          })}
      </Spin>
    );
  }
}
